// Blip's agent loop (docs/BLIP_HELPER_DESIGN.md, "The loop"), in the page:
// the conversation goes to the IDE's server (web/server/lib/blip.mjs), which
// puts the model, the system prompt, the tools and the limits around it and
// streams Claude's reply back; the page reads it with the Anthropic SDK's
// BetaMessageStream, carries out the tools Claude asks for, and sends their
// results back, until Claude ends its turn.
//
// The conversation only grows: Claude's messages are kept exactly as they
// came, thinking blocks and all, and nothing sent is changed later, since the
// API checks that a conversation's history is as it was. The exceptions are
// an empty message of Claude's, which the API refuses (repairConversation),
// a conversation's move to a newer knowledge pack (catchUp), and one grown
// too large to send (makeRoom).

import { BetaMessageStream } from "@anthropic-ai/sdk/lib/BetaMessageStream.mjs";
import { CHAT_PICTURES, chatPictures, chatTokens } from "./chats.js";

const MAX_STEPS = 30;

// The most a request may hold, in characters of its JSON: the server takes
// 12 MB (web/server/lib/blip.mjs), and a character can be more than a byte.
// A tool's picture that would take a request past it is left out.
const REQUEST_CHARACTERS = 9 << 20;

// The whole chat goes to the server at each step. When the user's next
// message takes it past the first of a pair, it is made smaller, to the
// second (makeRoom), so that it is not made smaller again soon: each time,
// Claude reads it all again rather than from the cache.
export const ROOM = {
  pictures: [16, 8],
  characters: [6 << 20, 3 << 20],
  messages: [400, 200],
  tokens: [150000, 75000],
};

const TOOL_PICTURE_GONE = "(A picture from earlier, taken out to keep the chat small: look again if you need it.)";
const USER_PICTURE_GONE = "(A picture the user showed earlier, taken out to keep the chat small.)";
const SHORTENED = "(This chat was shortened to keep it small: its earlier messages were taken out.";
const REQUESTS_KEPT = 20;

export class BlipError extends Error {
  // code: signin, allowance, global, pack, conversation, busy, setup,
  // connection, stopped, or the server's status
  constructor(message, { status = 0, code = "", renews = null } = {}) {
    super(message);
    this.status = status;
    this.code = code;
    this.renews = renews;
  }
}

const blocks = (content) => typeof content === "string" ? [{ type: "text", text: content }] : content;

// Claude may end a turn with an empty message, as after a tool's result when
// it has nothing to add. The API refuses a conversation holding one, so it
// is not kept; one an earlier version kept is taken out here, and the user's
// messages either side of it joined. Returns whether anything changed.
export function repairConversation(chat) {
  const kept = [];
  for (const message of chat.messages) {
    if (message.role === "assistant" && !blocks(message.content).length) continue;
    const last = kept[kept.length - 1];
    if (last && last.role === "user" && message.role === "user") {
      last.content = [...blocks(last.content), ...blocks(message.content)];
      continue;
    }
    kept.push(message);
  }
  if (kept.length === chat.messages.length) return false;
  chat.messages.splice(0, chat.messages.length, ...kept);
  return true;
}

// A conversation started with an older knowledge pack moves to the site's
// own (pack) at the user's next message, so Blip has the tools and notes
// published since: one started before the world tools would otherwise never
// have them. Each thinking block is bound to the system prompt and tools it
// was made with, so they all go; the API lets the thinking blocks from the
// start be taken out, and his words and tool calls stay. Not while the last
// message holds tool results, which go back with the thinking before their
// calls; the next message moves it instead. Returns whether it moved.
export function catchUp(chat, pack) {
  if (!pack || chat.pack === pack) return false;
  const last = chat.messages[chat.messages.length - 1];
  if (last && last.role === "user" && blocks(last.content).some((block) => block.type === "tool_result")) return false;
  for (const message of chat.messages) {
    if (message.role !== "assistant") continue;
    message.content = blocks(message.content).filter((block) => block.type !== "thinking" && block.type !== "redacted_thinking");
  }
  repairConversation(chat);
  chat.pack = pack;
  return true;
}

// The tools' pictures that fit in the chat's room for pictures, which the
// user's pictures share (CHAT_PICTURES), and in space, the characters left
// in a request: those beyond either are left out of their results, which say
// so. The user's next message makes room again (makeRoom).
export function fitPictures(results, room, space = Infinity) {
  for (const result of results) {
    if (!Array.isArray(result.content)) continue;
    let left = false;
    result.content = result.content.filter((part) => {
      if (part.type !== "image") return true;
      const size = part.source.data.length;
      if (room > 0 && size <= space) {
        --room;
        space -= size;
        return true;
      }
      left = true;
      return false;
    });
    if (left) {
      result.content.push({ type: "text", text: "(The picture was left out: this chat has no room for more pictures " +
        "until the user's next message. Work from what the tools say instead.)" });
    }
  }
  return results;
}

// Characters of a request's messages, as JSON.
const characters = (messages) => JSON.stringify(messages).length;

// Whether the conversation ends with tools' results Claude has not answered:
// they go back with the thinking before their calls, so nothing before them
// may change.
const awaitingAnswer = (chat) => {
  const last = chat.messages[chat.messages.length - 1];
  return !!last && last.role === "user" && blocks(last.content).some((block) => block.type === "tool_result");
};

// The room for the user's pictures in his next message: the pictures the
// chat keeps when it is made smaller leave the rest.
export function pictureRoom(chat) {
  if (!chat) return CHAT_PICTURES;
  const pictures = chatPictures(chat);
  return CHAT_PICTURES - (awaitingAnswer(chat) ? pictures : Math.min(pictures, ROOM.pictures[1]));
}

// The pictures in messages, oldest first: { content, index, tool }, content
// being the array that holds the picture at index.
function picturesIn(messages) {
  const pictures = [];
  for (const message of messages) {
    if (message.role !== "user" || !Array.isArray(message.content)) continue;
    message.content.forEach((block, index) => {
      if (block.type === "image") pictures.push({ content: message.content, index, tool: false });
      if (block.type !== "tool_result" || !Array.isArray(block.content)) return;
      block.content.forEach((part, at) => {
        if (part.type === "image") pictures.push({ content: block.content, index: at, tool: true });
      });
    });
  }
  return pictures;
}

// Takes pictures out of messages until at most most are left and they fit
// in size characters: the tools' first, then the user's, the oldest first.
// Each leaves a word in its place.
function takeOutPictures(messages, most, size) {
  const pictures = picturesIn(messages);
  let count = pictures.length, total = characters(messages);
  for (const picture of [...pictures.filter((one) => one.tool), ...pictures.filter((one) => !one.tool)]) {
    if (count <= most && total <= size) break;
    total -= picture.content[picture.index].source.data.length;
    picture.content[picture.index] = { type: "text", text: picture.tool ? TOOL_PICTURE_GONE : USER_PICTURE_GONE };
    --count;
  }
}

// A message that starts one of the user's requests, rather than bringing
// tools' results.
const startsRequest = (message) => message.role === "user" &&
  !blocks(message.content).some((block) => block.type === "tool_result");

// What the user asked in a request's message, without the IDE's context.
const requestWords = (message) => blocks(message.content).filter((block) => block.type === "text" && !block.text.startsWith("("))
  .map((block) => block.text.replace(/^<ide>[\s\S]*?<\/ide>\s*/, "").replace(/\s+/g, " ").trim()).filter(Boolean).join(" ");

// Takes out the oldest requests, with what came of them, until the rest fit
// target(measure); the last request always stays. The first left says what
// the user asked in those taken out, and keeps the pictures the user showed
// in them.
function shorten(chat, target) {
  // What the messages from each one on come to.
  const count = chat.messages.length;
  const from = new Array(count + 1).fill(null).map(() => ({ characters: 0, tokens: 0 }));
  for (let index = count - 1; index >= 0; --index) {
    const message = chat.messages[index];
    from[index] = { characters: from[index + 1].characters + characters([message]),
      tokens: from[index + 1].tokens + chatTokens({ messages: [message] }) };
  }
  const fits = (index) => count - index <= target("messages") && from[index].characters <= target("characters") &&
    from[index].tokens <= target("tokens");
  const starts = chat.messages.map((message, index) => index).filter((index) => index > 0 && startsRequest(chat.messages[index]));
  if (!starts.length || fits(0)) return;
  const cut = starts.find(fits) || starts[starts.length - 1];
  const gone = chat.messages.splice(0, cut);
  const requests = [];
  const pictures = [];
  for (const message of gone) {
    if (!startsRequest(message)) continue;
    for (const block of blocks(message.content)) {
      // An earlier shortening's list comes first.
      if (block.type === "text" && block.text.startsWith(SHORTENED)) requests.push(...(block.text.match(/^- .*$/gm) || []));
      if (block.type === "image") pictures.push(block);
    }
    const words = requestWords(message);
    if (words) requests.push(`- ${words.length > 300 ? `${words.slice(0, 300)}…` : words}`);
  }
  // The first, which is often what the chat is for, and the latest.
  const listed = requests.length > REQUESTS_KEPT ? [requests[0], ...requests.slice(1 - REQUESTS_KEPT)] : requests;
  const note = `${SHORTENED}${listed.length ? ` What the user asked in them, oldest first:\n${listed.join("\n")}\n` : ""})`;
  const first = chat.messages[0];
  first.content = [{ type: "text", text: note }, ...pictures, ...blocks(first.content)];
}

// Makes a conversation smaller, when it is past one of ROOM's limits, or
// when the server or Claude found it too large (force), and only while
// Claude is not answering tools' results. Each thinking block is bound to
// all that came before it, so all of them go, as the API lets them go from
// the start; then the tools' older pictures, then the user's, then the
// oldest requests. Returns whether it changed.
export function makeRoom(chat, force = false) {
  if (awaitingAnswer(chat)) return false;
  const past = (name, value) => value > ROOM[name][0];
  if (!force && !past("pictures", chatPictures(chat)) && !past("characters", characters(chat.messages)) &&
      !past("messages", chat.messages.length) && !past("tokens", chatTokens(chat))) return false;
  const target = (name) => force ? Math.floor(ROOM[name][1] / 2) : ROOM[name][1];
  for (const message of chat.messages) {
    if (message.role !== "assistant") continue;
    message.content = blocks(message.content).filter((block) => block.type !== "thinking" && block.type !== "redacted_thinking");
  }
  takeOutPictures(chat.messages, target("pictures"), target("characters"));
  shorten(chat, target);
  takeOutPictures(chat.messages, target("pictures"), target("characters"));
  repairConversation(chat);
  return true;
}

// Takes the pictures out of the last message, the user's, which Claude has
// not answered, so nothing depends on it. Returns whether it changed.
export function leaveOutLastPictures(chat) {
  const last = chat.messages[chat.messages.length - 1];
  if (!last || last.role !== "user" || !picturesIn([last]).length) return false;
  takeOutPictures([last], 0, 0);
  return true;
}

// Whether the server or Claude refused a conversation for its size.
export const tooLarge = (error) => error instanceof BlipError &&
  (error.status === 413 || /too (large|long)|too many pictures/i.test(error.message));

// Sends the conversation, and reads Claude's reply as it arrives.
async function step({ api, chat, level, events, signal }) {
  let response;
  try {
    response = await fetch(api, {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json", "X-B3D-IDE": "1" },
      body: JSON.stringify({ conversation: chat.id, pack: chat.pack, level, messages: chat.messages }),
      signal,
    });
  } catch (error) {
    if (signal.aborted) throw new BlipError("Stopped.", { code: "stopped" });
    throw new BlipError("Blip could not reach the server. Check the connection, and try again.", { code: "connection" });
  }
  if (!response.ok) {
    const result = (response.headers.get("content-type") || "").includes("json") ? await response.json() : {};
    throw new BlipError(result.error || `The server answered ${response.status}.`,
      { status: response.status, code: result.code || "", renews: result.renews || null });
  }
  const stream = BetaMessageStream.fromReadableStream(response.body);
  stream.on("thinking", (delta, snapshot) => events.thinking(snapshot));
  stream.on("text", (delta, snapshot) => events.text(snapshot));
  stream.on("streamEvent", (event) => {
    if (event.type === "content_block_start") events.block(event.content_block);
  });
  stream.on("error", () => {});
  try {
    return await stream.finalMessage();
  } catch {
    if (signal.aborted) throw new BlipError("Stopped.", { code: "stopped" });
    throw new BlipError("Blip lost his connection part-way. Try again.", { code: "connection" });
  }
}

// One request of the user's: content is the user's message's blocks, or
// none to send the conversation again as it is, after a failure. Runs
// until Claude ends its turn, with level's model (low, medium or high, or
// none for the server's own). events: { thinking(text), text(text),
// block(block), message(message), tool(call), toolDone(call, result) }.
// save() keeps the conversation. pack is the site's knowledge pack, which a
// new message moves the conversation to. maxSteps: the most steps, fewer
// for a TODO note (panel.js). Returns { reason: done, refusal, max_tokens,
// steps or stopped, message }.
export async function converse({ api, chat, level, content, tools, events, signal, save, pack = null, maxSteps = MAX_STEPS }) {
  if (content.length) catchUp(chat, pack);
  // A request stopped before Claude answered left the user's message last:
  // the new words join it, which changes nothing Claude has seen.
  repairConversation(chat);
  const last = chat.messages[chat.messages.length - 1];
  if (content.length) {
    if (last && last.role === "user") last.content = [...blocks(last.content), ...content];
    else chat.messages.push({ role: "user", content });
  } else if (!last || last.role !== "user") {
    return { reason: "done" };
  }
  makeRoom(chat);
  await save();
  // A conversation the server or Claude finds too large is made smaller,
  // and sent again once.
  const send = async () => {
    try {
      return await step({ api, chat, level, events, signal });
    } catch (error) {
      if (!tooLarge(error) || !(makeRoom(chat, true) || leaveOutLastPictures(chat))) throw error;
      await save();
      return await step({ api, chat, level, events, signal });
    }
  };
  for (let steps = 1; ; ++steps) {
    if (steps > maxSteps) return { reason: "steps" };
    const message = await send();
    // As plain data: exactly what is sent back.
    const reply = JSON.parse(JSON.stringify(message.content));
    // An empty reply ends the turn, but is not kept (repairConversation).
    if (reply.length) chat.messages.push({ role: "assistant", content: reply });
    events.message(message);
    const calls = reply.filter((block) => block.type === "tool_use");
    // A refused or cut-off message's tool calls are not run, but each still
    // needs a result for the conversation to go on.
    const unfinished = message.stop_reason === "refusal" || message.stop_reason === "max_tokens";
    if (calls.length && (unfinished || signal.aborted)) {
      chat.messages.push({ role: "user", content: calls.map((call) => ({ type: "tool_result", tool_use_id: call.id,
        content: unfinished ? "Not run: the message was cut off." : "Not run: the user pressed Stop.", is_error: true })) });
    }
    await save();
    if (signal.aborted) return { reason: "stopped", message };
    if (message.stop_reason === "refusal") return { reason: "refusal", message };
    if (message.stop_reason === "max_tokens") return { reason: "max_tokens", message };
    if (!calls.length) return { reason: "done", message };
    // The calls in order, except that those that may run at the same time
    // (tools.parallel: models and pictures, which wait for Tripo or OpenAI)
    // run together.
    const results = new Array(calls.length);
    const runCall = async (index) => {
      const call = calls[index];
      if (signal.aborted) {
        results[index] = { type: "tool_result", tool_use_id: call.id, content: "Not run: the user pressed Stop.", is_error: true };
        return;
      }
      events.tool(call);
      results[index] = await tools.run(call, signal);
      events.toolDone(call, results[index]);
    };
    for (let index = 0; index < calls.length;) {
      if (!tools.parallel || !tools.parallel(calls[index])) {
        await runCall(index++);
        continue;
      }
      const together = [];
      while (index < calls.length && tools.parallel(calls[index])) together.push(index++);
      await Promise.all(together.map(runCall));
    }
    // The results' pictures go in the room the rest of the request leaves.
    const words = characters(results.map((result) => Array.isArray(result.content) ?
      { ...result, content: result.content.filter((part) => part.type !== "image") } : result));
    chat.messages.push({ role: "user", content: fitPictures(results, CHAT_PICTURES - chatPictures(chat),
      REQUEST_CHARACTERS - characters(chat.messages) - words) });
    await save();
    if (signal.aborted) return { reason: "stopped", message };
  }
}
